import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, Rocket } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { useTranslation } from 'react-i18next';
import { fetchRemediation, approveRemediation, rejectRemediation, deployRemediation } from '../lib/api';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface Remediation {
  id: string;
  tenant_id: string;
  source: string;
  fleet_host_id: number;
  wazuh_agent_id: string;
  host_name: string;
  team_name: string;
  cve_id: string;
  sca_check_id: string;
  finding_type: string;
  software_name: string;
  software_version: string;
  severity: string;
  os_platform: string;
  ai_analysis: any;
  script_content: string;
  script_type: string;
  deploy_channel: string;
  validation_result: any;
  status: string;
  auto_approved: boolean;
  approved_by: string;
  error_message: string;
  secutools_job_id: string;
  ai_cost_cad: number;
  created_at: string;
  updated_at: string;
  deployed_at: string | null;
  completed_at: string | null;
}

const severityColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
};

export default function RemediationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [rem, setRem] = useState<Remediation | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchRemediation(id)
      .then(setRem)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const handleApprove = async () => {
    if (!id) return;
    setActing(true);
    try {
      await approveRemediation(id);
      toast.success(t('remediation.approved'));
      setRem((prev) => prev ? { ...prev, status: 'approved' } : null);
    } catch {
      toast.error(t('remediation.actionFailed'));
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (!id) return;
    setActing(true);
    try {
      await rejectRemediation(id);
      toast.success(t('remediation.rejected'));
      setRem((prev) => prev ? { ...prev, status: 'rejected' } : null);
    } catch {
      toast.error(t('remediation.actionFailed'));
    } finally {
      setActing(false);
    }
  };

  const handleDeploy = async () => {
    if (!id) return;
    setActing(true);
    try {
      await deployRemediation(id);
      toast.success(t('remediation.deployTriggered'));
      setRem((prev) => prev ? { ...prev, status: 'deploying' } : null);
    } catch {
      toast.error(t('remediation.actionFailed'));
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-96" />;
  }

  if (!rem) {
    return <p className="text-gray-500 dark:text-gray-400">{t('remediation.notFound')}</p>;
  }

  const aiAnalysis = typeof rem.ai_analysis === 'string' ? JSON.parse(rem.ai_analysis || '{}') : (rem.ai_analysis || {});
  const validation = typeof rem.validation_result === 'string' ? JSON.parse(rem.validation_result || '{}') : (rem.validation_result || {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/remediations')} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{rem.host_name}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {rem.finding_type.replace(/_/g, ' ')} &middot; {rem.id.slice(0, 8)}
          </p>
        </div>
        <StatusBadge status={rem.status} />
        <span className={`text-xs font-medium px-2 py-1 rounded ${severityColors[rem.severity] || ''}`}>
          {rem.severity}
        </span>
      </div>

      {/* Actions */}
      {(rem.status === 'pending' || rem.status === 'approved') && (
        <div className="flex gap-3">
          {rem.status === 'pending' && (
            <>
              <button onClick={handleApprove} disabled={acting} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                <Check className="w-4 h-4" /> {t('remediation.approve')}
              </button>
              <button onClick={handleReject} disabled={acting} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                <X className="w-4 h-4" /> {t('remediation.reject')}
              </button>
            </>
          )}
          {rem.status === 'approved' && (
            <button onClick={handleDeploy} disabled={acting} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              <Rocket className="w-4 h-4" /> {t('remediation.deploy')}
            </button>
          )}
        </div>
      )}

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Host Info */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('remediation.hostInfo')}</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('remediation.source')}</dt><dd className="text-gray-900 dark:text-gray-100">{rem.source}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('remediation.platform')}</dt><dd className="text-gray-900 dark:text-gray-100">{rem.os_platform || '-'}</dd></div>
            {rem.fleet_host_id > 0 && <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">Fleet Host ID</dt><dd className="text-gray-900 dark:text-gray-100">{rem.fleet_host_id}</dd></div>}
            {rem.wazuh_agent_id && <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">Wazuh Agent ID</dt><dd className="text-gray-900 dark:text-gray-100">{rem.wazuh_agent_id}</dd></div>}
            {rem.cve_id && <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">CVE</dt><dd className="text-gray-900 dark:text-gray-100 break-all">{rem.cve_id}</dd></div>}
            {rem.software_name && <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('remediation.software')}</dt><dd className="text-gray-900 dark:text-gray-100">{rem.software_name} {rem.software_version}</dd></div>}
            {rem.deploy_channel && <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('remediation.channel')}</dt><dd className="text-gray-900 dark:text-gray-100">{rem.deploy_channel}</dd></div>}
            <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('remediation.date')}</dt><dd className="text-gray-900 dark:text-gray-100">{rem.created_at ? format(new Date(rem.created_at), 'yyyy-MM-dd HH:mm') : '-'}</dd></div>
            {rem.deployed_at && <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('remediation.deployedAt')}</dt><dd className="text-gray-900 dark:text-gray-100">{format(new Date(rem.deployed_at), 'yyyy-MM-dd HH:mm')}</dd></div>}
            {rem.auto_approved && <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('remediation.autoApproved')}</dt><dd className="text-green-600">Yes</dd></div>}
            {rem.ai_cost_cad > 0 && <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('remediation.aiCost')}</dt><dd className="text-gray-900 dark:text-gray-100">${rem.ai_cost_cad.toFixed(4)} CAD</dd></div>}
          </dl>
        </div>

        {/* Validation */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('remediation.validation')}</h3>
          {validation.valid !== undefined ? (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${validation.valid ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-gray-900 dark:text-gray-100">{validation.valid ? t('remediation.validScript') : t('remediation.invalidScript')}</span>
              </div>
              {validation.errors?.length > 0 && (
                <div>
                  <p className="text-red-600 dark:text-red-400 font-medium">{t('remediation.errors')}:</p>
                  <ul className="list-disc pl-4 text-red-600 dark:text-red-400">{validation.errors.map((e: string, i: number) => <li key={i}>{e}</li>)}</ul>
                </div>
              )}
              {validation.warnings?.length > 0 && (
                <div>
                  <p className="text-yellow-600 dark:text-yellow-400 font-medium">{t('remediation.warnings')}:</p>
                  <ul className="list-disc pl-4 text-yellow-600 dark:text-yellow-400">{validation.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}</ul>
                </div>
              )}
              <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('remediation.errorHandling')}</dt><dd>{validation.has_error_handling ? '✓' : '✗'}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('remediation.logging')}</dt><dd>{validation.has_logging ? '✓' : '✗'}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('remediation.scriptSize')}</dt><dd>{validation.script_size_bytes} bytes</dd></div>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm">{t('remediation.noValidation')}</p>
          )}
          {rem.error_message && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-700 dark:text-red-300">
              <strong>{t('remediation.errorMsg')}:</strong> {rem.error_message}
            </div>
          )}
        </div>
      </div>

      {/* AI Analysis */}
      {aiAnalysis.summary && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('remediation.aiAnalysis')}</h3>
          <div className="space-y-3 text-sm">
            <div><strong className="text-gray-700 dark:text-gray-300">{t('remediation.summary')}:</strong> <span className="text-gray-900 dark:text-gray-100">{aiAnalysis.summary}</span></div>
            {aiAnalysis.risk_assessment && <div><strong className="text-gray-700 dark:text-gray-300">{t('remediation.riskAssessment')}:</strong> <span className="text-gray-900 dark:text-gray-100">{aiAnalysis.risk_assessment}</span></div>}
            {aiAnalysis.priority && <div><strong className="text-gray-700 dark:text-gray-300">{t('remediation.priority')}:</strong> <span className="capitalize text-gray-900 dark:text-gray-100">{aiAnalysis.priority}</span></div>}
            {aiAnalysis.script_explanation && <div><strong className="text-gray-700 dark:text-gray-300">{t('remediation.scriptExplanation')}:</strong> <span className="text-gray-900 dark:text-gray-100">{aiAnalysis.script_explanation}</span></div>}
            {aiAnalysis.rollback_plan && <div><strong className="text-gray-700 dark:text-gray-300">{t('remediation.rollbackPlan')}:</strong> <span className="text-gray-900 dark:text-gray-100">{aiAnalysis.rollback_plan}</span></div>}
            {aiAnalysis.remediation_steps?.length > 0 && (
              <div>
                <strong className="text-gray-700 dark:text-gray-300">{t('remediation.steps')}:</strong>
                <ol className="list-decimal pl-5 mt-1 space-y-1">
                  {aiAnalysis.remediation_steps.map((s: any, i: number) => (
                    <li key={i} className="text-gray-900 dark:text-gray-100">
                      {s.description}
                      {s.command && <code className="ml-2 text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">{s.command}</code>}
                      {s.risk && <span className={`ml-2 text-xs ${s.risk === 'high' ? 'text-red-500' : s.risk === 'medium' ? 'text-yellow-500' : 'text-green-500'}`}>({s.risk})</span>}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Script Content */}
      {rem.script_content && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
            {t('remediation.scriptContent')} <span className="text-xs text-gray-500 dark:text-gray-400">({rem.script_type})</span>
          </h3>
          <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-xs leading-relaxed max-h-96 overflow-y-auto">
            {rem.script_content}
          </pre>
        </div>
      )}
    </div>
  );
}
